import { FamilyRestroomOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Result from 'components/v2/common/Result';
import { ProfileFields } from './profileFields';
import * as S from '../ClientProfile.styled';
import { Relative } from '../ClientProfile';

import useEnabledCountries from 'hooks/useEnabledCountries';
import { RootState } from 'redux/store';

const FamilyMembers = ({
    familyData,
    mobile,
}: {
    familyData: null | Relative[];
    mobile: boolean;
}) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { hasSelfServeMarket } = useEnabledCountries();

    // Show only fields that have values — mirrors AssessmentV2 (e.g. child name
    // fields only when accompanying; country of birth only on UK domain).
    const hasValue = (val: unknown) => val !== null && val !== undefined && val !== '';

    function formatDate(date: Date | null) {
        if (date) {
            const stringDate = date.toString();
            const [year, month, day] = stringDate.split('-');
            const months =
                language === 'EN'
                    ? [
                          'January',
                          'February',
                          'March',
                          'April',
                          'May',
                          'June',
                          'July',
                          'August',
                          'September',
                          'October',
                          'November',
                          'December',
                      ]
                    : [
                          'Janvier',
                          'Février',
                          'Mars',
                          'Avril',
                          'Mai',
                          'Juin',
                          'Juillet',
                          'Août',
                          'Septembre',
                          'Octobre',
                          'Novembre',
                          'Décembre',
                      ];

            const monthName = months[parseInt(month, 10) - 1];
            const formattedDate = `${monthName} ${parseInt(day, 10)}, ${year}`;
            return formattedDate;
        } else {
            return '';
        }
    }

    return (
        <>
            {familyData && familyData.length > 0 ? (
                <>
                    {familyData.map((member: Relative, index: number) => {
                        const fields = [
                            { label: 'relation', value: member.relation, raw: member.relation },
                            { label: 'firstName', value: member.firstname, raw: member.firstname },
                            {
                                label: 'middleName',
                                value: member.middlename,
                                raw: member.middlename,
                            },
                            { label: 'lastName', value: member.lastname, raw: member.lastname },
                            {
                                label: 'dateOfBirth',
                                value: formatDate(member.birth_date),
                                raw: member.birth_date,
                            },
                            {
                                label: 'nationality',
                                value: member.nationality,
                                raw: member.nationality,
                            },
                            {
                                label: 'countryOfResidence',
                                value: member.country_of_residence,
                                raw: member.country_of_residence,
                            },
                            // AssessmentV2 only collects country of birth on UK and AU domains.
                            ...(hasSelfServeMarket
                                ? [
                                      {
                                          label: 'countryOfBirth',
                                          value: member.country_of_birth,
                                          raw: member.country_of_birth,
                                      },
                                  ]
                                : []),
                        ];

                        const visibleFields = fields.filter((f) => hasValue(f.raw));

                        return (
                            <div key={index}>
                                {familyData.length > 1 && (
                                    <S.Title>{`${t('familyMember', { lng: language })} ${
                                        index + 1
                                    }`}</S.Title>
                                )}
                                {visibleFields.map((f) => (
                                    <S.InfoItem key={f.label}>
                                        <Tooltip title={t(f.label, { lng: language })}>
                                            <S.Label mobile={mobile}>
                                                {t(f.label, { lng: language })}
                                            </S.Label>
                                        </Tooltip>
                                        <Tooltip title={f.value}>
                                            <S.Info mobile={mobile}>{f.value}</S.Info>
                                        </Tooltip>
                                    </S.InfoItem>
                                ))}
                                <ProfileFields
                                    record={member}
                                    keys={['accompanying']}
                                    mobile={mobile}
                                />
                            </div>
                        );
                    })}
                </>
            ) : (
                <Result
                    icon={<FamilyRestroomOutlined sx={{ fontSize: 32 }} />}
                    title={t('noInfo', { lng: language })}
                    description="No family members have been added yet."
                />
            )}
        </>
    );
};

export default FamilyMembers;
